import { useEffect, useRef, useState } from "react";
import { ArrowRight, Mail, Calendar, MessageSquare, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { FadeUp, MaskReveal } from "@/components/Motion";
import { openCalendly, CALENDLY_URL } from "@/lib/calendly";
import useSEO, { canonicalFor } from "@/lib/useSEO";

const TEXTURE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3e477c2a-f98b-44c6-92b8-bae42dd1c756/images/ba882160173d5126b25b664719240c651a32bbf5988a4e703e0e9dc729c0e269.png";

export default function Contact() {
  useSEO({
    title: "Contact — Corpora Consultants",
    description: "Speak with senior Cyprus counsel — book a consultation via Calendly, or submit an enquiry. Limassol · Nicosia · European Union.",
    canonical: canonicalFor("/contact"),
  });

  const { t, i18n } = useTranslation();
  const services = t("contact.services", { returnObjects: true });
  const calendlyRef = useRef(null);

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    company: "", jurisdiction: "", service_interest: "", message: "",
    website: "",  // honeypot — hidden from humans, bots fill it
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Init Calendly inline widget when the container mounts
  useEffect(() => {
    const tryInit = () => {
      if (calendlyRef.current && window.Calendly && window.Calendly.initInlineWidget) {
        // Clear previous embed if user switched language / re-visited
        calendlyRef.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url: `${CALENDLY_URL}?hide_gdpr_banner=1&background_color=0a0b0d&text_color=ede8de&primary_color=beaa8c`,
          parentElement: calendlyRef.current,
        });
        return true;
      }
      return false;
    };
    if (!tryInit()) {
      const id = setInterval(() => {
        if (tryInit()) clearInterval(id);
      }, 250);
      return () => clearInterval(id);
    }
  }, []);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...form,
        language: i18n.resolvedLanguage || i18n.language || "en",
      });
      toast.success(t("contact.successToast"));
      setSubmitted(true);
      setForm({
        first_name: "", last_name: "", email: "", phone: "",
        company: "", jurisdiction: "", service_interest: "", message: "",
        website: "",
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("contact.errorToast"));
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden grain">
        <img src={TEXTURE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.55)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,11,13,0.55) 0%, rgba(10,11,13,0.4) 30%, rgba(10,11,13,0.95) 100%)" }} />
        <div className="relative z-10 h-full max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-16">
          <h1 className="display text-[var(--bone)]" style={{ fontSize: "clamp(56px, 9vw, 140px)" }}>
            <div><MaskReveal>{t("contact.heroLine1")}</MaskReveal></div>
            <div className="display-italic" style={{ color: "var(--pewter)" }}>
              <MaskReveal delay={0.2}>{t("contact.heroLine2")}</MaskReveal>
            </div>
          </h1>
        </div>
      </section>

      {/* 3 CHANNELS */}
      <section className="py-20 md:py-28 border-b border-[var(--line)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="text-lg md:text-xl text-[var(--bone-2)] leading-relaxed max-w-3xl mb-14">
              {t("contact.channelsIntro")}
            </p>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="contact-channels">
            {/* Book a meeting */}
            <FadeUp delay={0.05}>
              <button
                type="button"
                onClick={() => openCalendly()}
                data-testid="contact-channel-book"
                className="text-left w-full h-full p-8 group transition-all duration-300 hover:border-[var(--pewter)]"
                style={{
                  background: "rgba(190,170,140,0.04)",
                  border: "1px solid var(--line-2)",
                  borderRadius: 8,
                  minHeight: 260,
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <Calendar size={22} style={{ color: "var(--pewter)" }} />
                  <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[var(--mute-2)]">
                    {t("contact.channelBookTime")}
                  </span>
                </div>
                <h3 className="display text-3xl text-[var(--bone)] leading-tight mb-3 group-hover:text-[var(--pewter)] transition-colors">
                  {t("contact.channelBookTitle")}
                </h3>
                <p className="text-[var(--bone-2)] leading-relaxed text-sm">
                  {t("contact.channelBookBody")}
                </p>
                <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--pewter)] inline-flex items-center gap-2">
                  {t("contact.channelBookCta")} <ArrowRight size={12} />
                </div>
              </button>
            </FadeUp>

            {/* Email */}
            <FadeUp delay={0.15}>
              <a
                href="mailto:info@corporaco.com?subject=Enquiry%20%E2%80%94%20Corporaco"
                data-testid="contact-channel-email"
                className="block w-full h-full p-8 group transition-all duration-300 hover:border-[var(--pewter)]"
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid var(--line-2)",
                  borderRadius: 8,
                  minHeight: 260,
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <Mail size={22} style={{ color: "var(--pewter)" }} />
                  <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[var(--mute-2)]">
                    {t("contact.channelEmailReply")}
                  </span>
                </div>
                <h3 className="display text-3xl text-[var(--bone)] leading-tight mb-3 group-hover:text-[var(--pewter)] transition-colors">
                  {t("contact.channelEmailTitle")}
                </h3>
                <p className="text-[var(--bone-2)] leading-relaxed text-sm">
                  {t("contact.channelEmailBody")}
                </p>
                <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--pewter)] break-all">
                  info@corporaco.com
                </div>
              </a>
            </FadeUp>

            {/* Form */}
            <FadeUp delay={0.25}>
              <button
                type="button"
                onClick={() => scrollTo("enquiry-form")}
                data-testid="contact-channel-form"
                className="text-left w-full h-full p-8 group transition-all duration-300 hover:border-[var(--pewter)]"
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid var(--line-2)",
                  borderRadius: 8,
                  minHeight: 260,
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <MessageSquare size={22} style={{ color: "var(--pewter)" }} />
                  <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[var(--mute-2)]">
                    {t("contact.channelFormReply")}
                  </span>
                </div>
                <h3 className="display text-3xl text-[var(--bone)] leading-tight mb-3 group-hover:text-[var(--pewter)] transition-colors">
                  {t("contact.channelFormTitle")}
                </h3>
                <p className="text-[var(--bone-2)] leading-relaxed text-sm">
                  {t("contact.channelFormBody")}
                </p>
                <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--pewter)] inline-flex items-center gap-2">
                  {t("contact.channelFormCta")} <ArrowRight size={12} />
                </div>
              </button>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* CALENDLY INLINE EMBED */}
      <section className="py-20 md:py-28 border-b border-[var(--line)]" id="book-meeting">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <FadeUp>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--mute-2)] mb-4">
                  {t("contact.calendlyEyebrow")}
                </div>
                <h2 className="display text-4xl md:text-5xl text-[var(--bone)] leading-[1.05] max-w-2xl">
                  {t("contact.calendlyTitleA")}{" "}
                  <span className="display-italic" style={{ color: "var(--pewter)" }}>
                    {t("contact.calendlyTitleB")}
                  </span>
                </h2>
              </div>
              <p className="text-[var(--bone-2)] max-w-md">
                {t("contact.calendlyBody")}
              </p>
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div
              ref={calendlyRef}
              data-testid="contact-calendly-inline"
              className="calendly-inline-widget"
              style={{
                minWidth: "320px",
                height: "720px",
                border: "1px solid var(--line)",
                borderRadius: 6,
                overflow: "hidden",
                background: "rgba(10,11,13,0.5)",
              }}
            />
          </FadeUp>
        </div>
      </section>

      {/* FORM */}
      <section className="py-20 md:py-28 border-b border-[var(--line)]" id="enquiry-form">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <FadeUp>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--mute-2)] mb-4">
                {t("contact.formEyebrow")}
              </div>
              <h2 className="display text-4xl md:text-5xl text-[var(--bone)] leading-[1.05] mb-8">
                {t("contact.formTitle")}
              </h2>
              <p className="text-[var(--bone-2)] leading-relaxed mb-10 max-w-md">
                {t("contact.formSubtitle")}
              </p>

              <div className="border-t border-[var(--line)] pt-8">
                <p className="display text-2xl italic leading-snug text-[var(--bone)]" style={{ fontWeight: 300 }}>
                  {t("contact.discretionA")}{" "}
                  <span style={{ color: "var(--pewter)" }}>{t("contact.discretionB")}</span>{" "}
                  {t("contact.discretionC")}{" "}
                  <span style={{ color: "var(--pewter)" }}>{t("contact.discretionD")}</span>.
                </p>
              </div>
            </FadeUp>
          </div>

          <div className="md:col-span-7">
            {submitted ? (
              <FadeUp>
                <div data-testid="contact-success" className="border border-[var(--pewter)] p-10" style={{ background: "var(--pewter-mute)" }}>
                  <h3 className="display text-3xl text-[var(--bone)]">{t("contact.successTitle")}</h3>
                  <p className="mt-4 text-[var(--bone-2)]">{t("contact.successBody")}</p>
                  <div className="mt-8 flex flex-wrap gap-4">
                    <button onClick={() => setSubmitted(false)} className="btn-ghost" data-testid="contact-send-another">
                      {t("contact.sendAnother")}
                    </button>
                    <button type="button" onClick={() => openCalendly()} className="btn-secondary" data-testid="contact-success-book">
                      {t("contact.successBookNow")}
                    </button>
                  </div>
                </div>
              </FadeUp>
            ) : (
              <FadeUp delay={0.1}>
                <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="contact-form">
                  <div>
                    <label className="field-label block mb-2">{t("contact.firstName")} *</label>
                    <input name="first_name" required value={form.first_name} onChange={onChange} className="field" data-testid="contact-first-name" />
                  </div>
                  <div>
                    <label className="field-label block mb-2">{t("contact.lastName")} *</label>
                    <input name="last_name" required value={form.last_name} onChange={onChange} className="field" data-testid="contact-last-name" />
                  </div>
                  <div>
                    <label className="field-label block mb-2">{t("contact.email")} *</label>
                    <input type="email" name="email" required value={form.email} onChange={onChange} className="field" data-testid="contact-email" />
                  </div>
                  <div>
                    <label className="field-label block mb-2">{t("contact.phone")}</label>
                    <input name="phone" value={form.phone} onChange={onChange} className="field" data-testid="contact-phone" />
                  </div>
                  <div>
                    <label className="field-label block mb-2">{t("contact.company")}</label>
                    <input name="company" value={form.company} onChange={onChange} className="field" data-testid="contact-company" />
                  </div>
                  <div>
                    <label className="field-label block mb-2">{t("contact.jurisdiction")}</label>
                    <input name="jurisdiction" placeholder={t("contact.jurisdictionPlaceholder")} value={form.jurisdiction} onChange={onChange} className="field" data-testid="contact-jurisdiction" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label block mb-2">{t("contact.service")}</label>
                    <select name="service_interest" value={form.service_interest} onChange={onChange} className="field" data-testid="contact-service">
                      <option value="" style={{ background: "var(--ink)" }}>
                        {t("contact.selectService")}
                      </option>
                      {services.map((s) => (
                        <option key={s} value={s} style={{ background: "var(--ink)" }}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label block mb-2">{t("contact.details")} *</label>
                    <textarea name="message" required value={form.message} onChange={onChange} rows={5} className="field" placeholder={t("contact.detailsPlaceholder")} data-testid="contact-message" />
                  </div>
                  {/* Honeypot — hidden from humans via CSS + aria, bots will fill this and be rejected */}
                  <div
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
                  >
                    <label>Website (leave blank)</label>
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={onChange}
                    />
                  </div>
                  <div className="md:col-span-2 mt-4 flex flex-wrap gap-4 items-center">
                    <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="contact-submit">
                      {submitting ? t("contact.submitting") : t("contact.submit")} <ArrowRight size={14} />
                    </button>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--mute-2)]">
                      {t("contact.formReplyTime")}
                    </span>
                  </div>
                </form>
              </FadeUp>
            )}
          </div>
        </div>
      </section>

      {/* OFFICE DETAILS */}
      <section className="py-20 md:py-24">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          <FadeUp>
            <div className="flex items-start gap-4">
              <MapPin size={18} style={{ color: "var(--pewter)" }} className="mt-1 shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--mute-2)] mb-3">
                  {t("contact.officeEyebrow")}
                </div>
                <address className="not-italic text-[var(--bone-2)] leading-relaxed font-light">
                  Ischyron, Kato Polemidia<br />
                  4151, Limassol<br />
                  Cyprus
                </address>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="flex items-start gap-4">
              <Mail size={18} style={{ color: "var(--pewter)" }} className="mt-1 shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--mute-2)] mb-3">
                  {t("contact.directEyebrow")}
                </div>
                <a href="mailto:info@corporaco.com" className="link-under text-[var(--bone)] block" data-testid="contact-email-link">
                  info@corporaco.com
                </a>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.2}>
            <div className="flex items-start gap-4">
              <Clock size={18} style={{ color: "var(--pewter)" }} className="mt-1 shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--mute-2)] mb-3">
                  {t("contact.hoursEyebrow")}
                </div>
                <p className="text-[var(--bone-2)] leading-relaxed font-light whitespace-pre-line">
                  {t("contact.hours")}
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
